import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { Label } from '../ui/label.tsx';
import { useUpdateStaff } from '../../api/staff/staffQueries';
import { toast } from 'react-toastify';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select.tsx';
import { SlidePanel } from '../ui/slide-panel';
import { LoadingSpinner } from '../ui/loading-spinner.tsx';
import { Calendar } from '../ui/calendar.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover.tsx';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '../../lib/utils';

export function StaffEditPanel({ staff, isOpen, onClose }) {
  const { mutate: updateStaff, isPending: isUpdating } = useUpdateStaff();
  const [staffId, setStaffId] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [designation, setDesignation] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [dob, setDob] = useState(null);
  const [bloodGroup, setBloodGroup] = useState('');
  const [address, setAddress] = useState('');
  const [salary, setSalary] = useState('');
  const [photoUrl, setPhotoUrl] = useState('/placeholder.svg');
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (staff) {
      console.log('Staff data received in edit panel:', staff); // For debugging

      // Always use staffId as the primary identifier
      const id = staff.staffId || staff.id;
      setStaffId(id);

      // Set all form fields from staff data with proper fallbacks
      setFirstName(staff.firstName || '');
      setLastName(staff.lastName || '');
      setDesignation(staff.designation || '');
      setDepartmentId(staff.departmentId || '');
      setDob(staff.dob ? new Date(staff.dob) : null);
      setBloodGroup(staff.bloodGroup || '');
      setAddress(staff.address || '');
      setSalary(staff.salary || '');
      setPhotoUrl(staff.photoUrl || staff.avatar || '/placeholder.svg');

      // Log the initialized state for verification
      console.log('Initialized edit form with:', {
        id,
        firstName: staff.firstName,
        lastName: staff.lastName,
        designation: staff.designation,
        departmentId: staff.departmentId,
        dob: staff.dob,
        bloodGroup: staff.bloodGroup,
        address: staff.address,
        salary: staff.salary,
      });
    }
  }, [staff]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!staffId) {
      toast.error('Staff ID is missing');
      return;
    }

    const data = {
      firstName,
      lastName,
      designation,
      departmentId,
      dob: dob,
      bloodGroup,
      address,
      salary,
    };

    console.log('Submitting staff update:', data); // For debugging

    updateStaff([staffId, data], {
      onSuccess: () => {
        onClose();
      },
    });
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleChangeImage = () => {
    fileInputRef.current?.click();
  };

  const renderFooter = () => (
    <div className="flex justify-center gap-4">
      <Button
        type="button"
        variant="outline"
        className="w-full sm:w-40 text-purple-500 border-purple-200"
        onClick={onClose}
        disabled={isUpdating}
      >
        Cancel
      </Button>
      <Button
        type="submit"
        form="staff-edit-form"
        className="w-full sm:w-40 bg-purple-500 hover:bg-purple-600 text-white"
        disabled={isUpdating}
      >
        {isUpdating ? (
          <div className="flex items-center gap-2">
            <LoadingSpinner size="sm" className="text-white" />
            Saving...
          </div>
        ) : (
          'Save Changes'
        )}
      </Button>
    </div>
  );

  const bloodGroupOptions = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

  return (
    <SlidePanel
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Staff Details"
      footer={renderFooter()}
    >
      <form
        id="staff-edit-form"
        onSubmit={handleSubmit}
        className="h-full flex flex-col"
        autoComplete="on"
      >
        <div className="flex-1 p-6 space-y-8">
          <input
            type="file"
            id="staff-photo-upload"
            ref={fileInputRef}
            className="hidden"
            accept="image/*"
            onChange={handleImageSelect}
            aria-label="Upload staff photo"
          />

          <div className="flex flex-col items-center">
            <div className="relative mb-4">
              <div className="h-24 w-24 rounded-full overflow-hidden border-4 border-gray-200">
                <img
                  src={photoUrl}
                  alt="Staff profile"
                  className="object-cover w-full h-full"
                />
              </div>
            </div>
            <Button
              type="button"
              className="bg-purple-500 hover:bg-purple-600 text-white"
              onClick={handleChangeImage}
              aria-label="Change profile image"
            >
              Change Image
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="staff-first-name">First Name</Label>
              <Input
                id="staff-first-name"
                name="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                autoComplete="given-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="staff-last-name">Last Name</Label>
              <Input
                id="staff-last-name"
                name="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                autoComplete="family-name"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="staff-designation">Designation</Label>
            <Input
              id="staff-designation"
              name="designation"
              value={designation}
              onChange={(e) => setDesignation(e.target.value)}
              placeholder="e.g. Senior Teacher"
            />
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="staff-blood-group">Blood Group</Label>
              <Select value={bloodGroup} onValueChange={setBloodGroup}>
                <SelectTrigger id="staff-blood-group">
                  <SelectValue placeholder="Select blood group" />
                </SelectTrigger>
                <SelectContent>
                  {bloodGroupOptions.map((group) => (
                    <SelectItem key={group} value={group}>
                      {group}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Date of Birth</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant={'outline'}
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !dob && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dob ? format(dob, 'PPP') : <span>Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={dob}
                    onSelect={setDob}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="staff-address">Address</Label>
            <Input
              id="staff-address"
              name="address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Complete address"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="staff-salary">Salary</Label>
            <Input
              id="staff-salary"
              name="salary"
              type="text"
              value={salary}
              onChange={(e) => setSalary(e.target.value)}
              placeholder="Enter salary amount"
            />
          </div>
        </div>
      </form>
    </SlidePanel>
  );
}
